import { useAppSelector } from '~/redux/hooks';
import { RootState } from '~/redux/store';

const DEFAULT_RESTORABLE_SELECTOR = () => undefined;

/**
 * Creates a hook that returns a holder for either a user selected value or a default
 */
export const createSelectedHook =
  <SelectedType, ApiHookResponse, RestorableType>(createSelectedHookOptions: {
    /**
     * Selector to get the user selected value from Redux store
     */
    selector: (state: RootState) => SelectedType;
    /**
     * Selector to get the restorable value from Redux store
     */
    restorableSelector?: (state: RootState) => RestorableType;
    /**
     * Hook to fetch api data from RTK Query
     */
    apiQueryHook: () => ApiHookResponse;
    /**
     * Callback that determines whether the default value should be returned instead of the user selected value
     */
    shouldReturnDefault?: (arg: {
      selectedValue: SelectedType;
      apiHookResponse: ApiHookResponse;
    }) => boolean;
    /**
     * Callback to get the default value from the {@link selectedValue} and {@link apiHookResponse}
     */
    selectDefaultValue?: (arg: {
      selectedValue: SelectedType;
      apiHookResponse: ApiHookResponse;
    }) => SelectedType;

    /**
     * Callback to decide how to best use the restorableValue - returns an actual selection record
     */
    selectRestorableValue?: (arg: {
      restorableValue: RestorableType;
      selectedValue: SelectedType;
      apiHookResponse: ApiHookResponse;
    }) => SelectedType;
  }): (() => { selectedValue?: SelectedType }) =>
  () => {
    const {
      selector,
      restorableSelector,
      apiQueryHook,
      shouldReturnDefault = () => false,
      selectDefaultValue = (arg) => arg.selectedValue,
      selectRestorableValue = (_arg) => undefined,
    } = createSelectedHookOptions;

    const selectedValue = useAppSelector(selector);
    const apiHookResponse = apiQueryHook();

    const restorableValue = useAppSelector(
      restorableSelector ?? DEFAULT_RESTORABLE_SELECTOR
    );

    const restoredValue = restorableValue
      ? selectRestorableValue({
          restorableValue,
          selectedValue,
          apiHookResponse,
        })
      : undefined;

    const _shouldReturnDefault = shouldReturnDefault({
      selectedValue,
      apiHookResponse,
    });
    const defaultValue = selectDefaultValue({ selectedValue, apiHookResponse });

    return {
      selectedValue: _shouldReturnDefault
        ? (restoredValue ?? defaultValue)
        : selectedValue,
    };
  };
